import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import { isIOS } from 'react-device-detect';

import * as QPlayerSVGElement from '../QPlayerSVGElement';
import { getEmbedShareSettings, joinClassNames, STORY_CONFIG_ID } from '../../util/util';
import { NumberHelper } from '../../util/number-helper';

import ControlButton from './ControlButton';
import AiTag from './AiTag';
import TopControls from './TopControls/TopControls';
import BottomControls from './BottomControls/BottomControls';
import CenterControls from './CenterControls/CenterControls';
import { StoryTopControl } from './TopControls/StoryTopControl';
import { SideControls } from './TopControls/SideControls';
import { StoryCenterControls } from './CenterControls/StoryCenterControls';
import { CenteredPlayPauseOverlay } from './CenteredPlayPauseOverlay/CenteredPlayPauseOverlay';
import LiveTranscript from '../LiveTranscript/LiveTranscript';

import './Controls.scss';
import { usePlayer } from '../../contexts/PlayerContext';
import { useConfiguration } from '../../contexts/ConfigurationContext';

interface ControlsProps {
	settings: any;
	playerHover?: boolean;
	isMobile?: boolean;
	playerId?: string;
	isInlinePlayer?: boolean;
	isMaintainProportions?: boolean;
	container?: HTMLElement | null;
	sideWindowOpen?: boolean;
	onTouchStart?: (_: React.TouchEvent) => void;
	onTouchMove?: (_: React.TouchEvent) => void;
	onMouseEnter?: (_: React.MouseEvent) => void;
	onMouseLeave?: (_: React.MouseEvent) => void;
	onFocus?: (_: React.FocusEvent) => void;
	onBlur?: (_: React.FocusEvent) => void;
	alignVideoOnAndroidDevices?: (_timeOut: number, _isReset: boolean) => void;
}

const Controls: React.FC<ControlsProps> = ({
	settings,
	playerHover,
	isMobile,
	isInlinePlayer,
	isMaintainProportions,
	sideWindowOpen,
	onTouchStart,
	onTouchMove,
	onMouseEnter,
	onMouseLeave,
	onFocus,
	onBlur,
	alignVideoOnAndroidDevices,
}) => {
	const { t } = useTranslation();

	const { adsPlaying, isPlaying, currentVolumeLevel, isLiveStopped, currentPosition, currentSceneWidgets, currentInteractiveElement } = usePlayer();
	const {
		currentMediaId,
		playlistId,
		showCountdownModule,
		mediaType,
		embedShareSettings: embedShareSettingsMessage,
		minimizedPlayer,
		mediasData,
		configParams,
		isMediaLive,
		subtitleLanguage,
		liveTranscriptLanguage,
		enabledLiveTranscript,
	} = useConfiguration();

	const container = !configParams?.qplayerPreview ? configParams?.container ?? document : document;
	const currentMedia = useMemo(
		() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
		[mediasData, currentMediaId]
	);
	const isStoryPlayer = typeof configParams?.configId === 'string' && configParams.configId.toLowerCase().includes(STORY_CONFIG_ID.toLowerCase());
	const color = useMemo(() => settings?.playerStyling?.primaryColor ?? '#126cfc', [settings?.playerStyling?.primaryColor]);
	const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
	const embedShareSettings = getEmbedShareSettings({ configParams, messageData: embedShareSettingsMessage });

	const showTopControls = useMemo(
		() =>
			settings?.topControls?.enabled ||
			settings?.controls?.enabled ||
			configParams.sharing === 'true' ||
			configParams.downloadMedia === 'true' ||
			configParams.openInNewWindow === 'true',
		[configParams, settings?.topControls?.enabled, settings?.controls?.enabled]
	);

	const showSkipLink = useMemo(() => {
		const interactiveElements = mediaType === 'scenario' ? currentSceneWidgets : currentInteractiveElement;
		return interactiveElements && interactiveElements.length > 0;
	}, [currentInteractiveElement, currentSceneWidgets, mediaType]);

	const [showUnmuteButton, setShowUnmuteButton] = useState(
		settings?.playbackOptions.showUnmuteBtn !== false &&
			(configParams.mute === 'true' || (!configParams.mute && settings?.playbackOptions.muteAtStart)) &&
			currentVolumeLevel === 0
	);

	const onUnmuteClick = useCallback(() => {
		setTimeout(() => {
			const qplayerAppElem = container.querySelector('div.qplayer-app') as any;
			qplayerAppElem?.focus();
		});

		if (isIOS) {
			// IOSs sometime does not trigger click event. It seem the touch end event swallows onClick event. So handle volume on App.tsx for IOS.
			return;
		}

		const currentPlayerId = `player-${playlistId || currentMediaId}-${configParams.containerKey}`;
		(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(0.5);
	}, [currentMediaId, playlistId, configParams.containerKey]);

	const calculatePlayerSize = useCallback(() => {
		if (!currentMedia || !currentMedia.videoWidth || !currentMedia.videoHeight) {
			return;
		}

		const videoRatio = Math.floor((currentMedia.videoHeight / currentMedia.videoWidth) * 100) / 100;
		const shouldCalculateTheSize =
			videoRatio === Math.floor((16 / 9) * 100) / 100 ||
			configParams?.fromQplayerWidget === 'true' ||
			(configParams?.useLoader === 'true' && isStoryPlayer)
				? true
				: isMobile;
		if (!shouldCalculateTheSize) {
			return;
		}

		const currentPlayerId = `player-${isStoryPlayer ? currentMediaId : playlistId || currentMediaId}-${configParams.containerKey}`;
		if (
			currentPlayerId &&
			!isInlinePlayer &&
			(isMobile || configParams?.fromQplayerWidget === 'true' || (configParams?.useLoader === 'true' && isStoryPlayer))
		) {
			setTimeout(() => {
				const playerElem = container.querySelector(`#${currentPlayerId}`);
				const transitionElem = container.querySelector('.transition-wrapper') as any;
				const interactContainer = container.querySelector('.interactOverlayContainer') as any;
				if (!playerElem) {
					return;
				}

				const screenHeight = embedShareSettings?.screenHeight ?? window.screen.height;
				const screenWidth = embedShareSettings?.screenWidth ?? window.screen.width;
				const ratio = isMaintainProportions === false || configParams?.fromQplayerWidget === 'true' ? screenHeight / screenWidth : videoRatio;
				const width = `calc(${ratio} * 100vh)`;

				playerElem.style.width = width;
				if (interactContainer && (isMaintainProportions === false || configParams?.fromQplayerWidget === 'true')) {
					interactContainer.style.width = width;
					if (transitionElem) {
						transitionElem.style.width = width;
					}
				}
			});
		}
	}, [
		currentMedia,
		configParams?.fromQplayerWidget,
		configParams?.useLoader,
		configParams.containerKey,
		isStoryPlayer,
		isMobile,
		currentMediaId,
		playlistId,
		isInlinePlayer,
		container,
		embedShareSettings?.screenHeight,
		embedShareSettings?.screenWidth,
		isMaintainProportions,
	]);

	const handleFullscreenchange = useCallback(() => {
		const isFullScreen =
			!!document.fullscreenElement ||
			(document as any).fullscreen ||
			(document as any).isFullScreen ||
			(document as any).msIsFullScreen ||
			(document as any).mozIsFullScreen ||
			(document as any).webkitIsFullScreen;
		if (!isFullScreen) {
			calculatePlayerSize();
		}
	}, [calculatePlayerSize]);

	const onSkipToMainKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			location.hash = "#mainSVG";
			(document.querySelector('#mainSVG') as any)?.focus();
		}
	}, []);

	useEffect(() => {
		let timeout: any = null;
		if (currentVolumeLevel > 0) {
			timeout = setTimeout(() => setShowUnmuteButton(false), 500);
		}
		return () => {
			timeout && clearTimeout(timeout);
		};
	}, [currentVolumeLevel]);

	useLayoutEffect(() => {
		calculatePlayerSize();
	}, [
		isMobile,
		isMaintainProportions,
		embedShareSettings.screenHeight,
		embedShareSettings.screenWidth,
		isInlinePlayer,
		embedShareSettings,
		playlistId,
		currentMediaId,
		isStoryPlayer,
		calculatePlayerSize,
	]);

	useEffect(() => {
		window.addEventListener('fullscreenchange', handleFullscreenchange);

		return () => {
			window.removeEventListener('fullscreenchange', handleFullscreenchange);
		};
	}, [handleFullscreenchange]);

	const renderUnmuteButton = () =>
		isPlaying && (
			<ControlButton
				tabIndex={0}
				vertical
				className={joinClassNames(
					showUnmuteButton ? '' : 'controlbar__control--vertical-hidden',
					isMobile ? 'controlbar__control--vertical-mobile' : '',
					'mute-button'
				)}
				icon={currentVolumeLevel > 0 ? <QPlayerSVGElement.Mute /> : <QPlayerSVGElement.Unmute />}
				onClick={showUnmuteButton ? onUnmuteClick : undefined}
				hoverStyle={hoverStyle}
			>
				{t('UNMUTE_BUTTON')}
			</ControlButton>
		);

	const showPostCover =
		!isLiveStopped || typeof isLiveStopped === 'boolean'
			? isLiveStopped
			: (isPlaying === undefined || (isPlaying && mediaType === 'scenario')) &&
				Math.ceil(NumberHelper.precision3(typeof currentPosition === 'number' ? currentPosition : 0)) ===
					Math.ceil(NumberHelper.precision3(isLiveStopped));

	// `showPostCover` is the live "this broadcast has ended" state, and on a normal preset the bar
	// gets out of the way so the cover can take over. A Story player has no post cover to put there,
	// so the flag only strips the reel's own chrome - timeline, mute, share - and leaves nothing.
	// That is what a finished live *recording* in a feed hit: `playVisiblePlayer` reads
	// `custom.livemanager.stop` off the clip without asking whether the media is live at all, so a
	// recording of an ended event reads as an ended event. Scoped to the Story player on purpose:
	// the shared live logic is untouched, so no other preset's post cover changes behaviour.
	return minimizedPlayer || showCountdownModule || (showPostCover && !isStoryPlayer) || adsPlaying ? null : (
		<div
			className={joinClassNames(
				'controlsOverlay',
				isMobile ? 'controlsOverlay--mobile' : '',
				isInlinePlayer ? 'controlsOverlay--inline' : '',
				isStoryPlayer ? 'controlsOverlay--story' : ''
			)}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onFocus={onFocus}
			onBlur={onBlur}
		>
			{showSkipLink && <a
				href="#mainSVG"
				className="qplayer--skip-link"
				style={hoverStyle}
				onKeyDown={onSkipToMainKeyDown}
			>
				{t("SKIP_LINK_LABEL")}
			</a>}

			{!isStoryPlayer && renderUnmuteButton()}

			{isStoryPlayer ? (
				showTopControls && (
					<StoryTopControl
						className={joinClassNames([
							'controlsOverlayTopControls',
							settings?.controls?.alwaysVisible || playerHover ? 'controlsOverlayVisible' : '',
						])}
						settings={settings}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						isMobile={isMobile}
					/>
				)
			) : (
				<TopControls
					className={joinClassNames([
						'controlsOverlayTopControls',
						settings?.topControls?.alwaysVisible || playerHover ? 'controlsOverlayVisible' : '',
					])}
					settings={settings}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					isInlinePlayer={isInlinePlayer}
					isMobile={isMobile}
					showTopControls={showTopControls}
				/>
			)}

			{isStoryPlayer && (
				<SideControls
					className={settings?.controls.alwaysVisible || playerHover ? 'controlsOverlayVisible' : ''}
					settings={settings}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					isMobile={isMobile}
					playerHover={playerHover}
				/>
			)}

			{isStoryPlayer ? (
				<StoryCenterControls playerHover={playerHover} isMobile={isMobile} settings={settings} />
			) : (
				<CenterControls playerHover={playerHover} isMobile={isMobile} settings={settings} isInlinePlayer={isInlinePlayer} />
			)}

			{!isStoryPlayer && (
				<CenteredPlayPauseOverlay
					playerHover={playerHover}
					isMobile={isMobile}
					settings={settings}
					isInlinePlayer={isInlinePlayer}
				/>
			)}

			{!isStoryPlayer && (
				<BottomControls
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					playerHover={playerHover}
					settings={settings}
					isInlinePlayer={isInlinePlayer}
					isMaintainProportions={isMobile ? isMaintainProportions : undefined}
					alignVideoOnAndroidDevices={alignVideoOnAndroidDevices}
				/>
			)}
			{isStoryPlayer && settings?.additionalSettings?.aiTagging && <AiTag style={{ bottom: 8 }} />}
			{isMediaLive && liveTranscriptLanguage === subtitleLanguage.code && enabledLiveTranscript && <LiveTranscript sideWindowOpen={sideWindowOpen} playerHover={!!playerHover}/>}
		</div>
	);
};

export default Controls;
